/* eslint-disable @typescript-eslint/no-explicit-any */
import PostDetail from '@/components/BlogDetail'
import CategoryPage from '@/components/CategoryPage'
import PageBreadcrumb from '@/components/PageBreadcrumb'
import ProductDetailPage from '@/components/ProductDetailPage'
import { PageType } from '@/constants'
import { useAuth, useCart, useStore } from '@/containers'
import { Meta } from '@/layouts/Meta'
import { Main } from '@/templates/Main'
import type { BannerList, Post, Promotion, QueryParam } from '@/types'
import { getTranslate, setContext } from '@/utils/api-interceptors'
import {
  getBannerSSR,
  getBottomMenu,
  getCategorySSR,
  getExclusiveCatDeal,
  getHeaderMenu,
  getKeyword,
  getPageDetailSSR,
  getPath,
  getPostCategory,
  getSaleRegion,
} from '@/utils/endpoint'
import * as pixel from '@/utils/fpixel'
import { getPageTypeFromPath } from '@/utils/next-util'
import { getCookies } from 'cookies-next'
import { type GetServerSideProps, type GetServerSidePropsContext } from 'next'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/router'
import { useEffect } from 'react'

interface DynamicPageProps {
  pageDetail: any
  categoryDetail: any
  banner: BannerList[]
  pageType: string
  hideMenu: string
  tips: Post[]
  promotions: Post[]
  exclusiveDeal: Promotion
  postCategory: Post[]
}
const DynamicPage = ({
  pageDetail,
  categoryDetail,
  pageType,
  banner,
  hideMenu,
  exclusiveDeal,
  postCategory,
}: DynamicPageProps) => {
  const t = useTranslations('ProductDetail')
  const { setIsOpenComparePopup, getDistricts } = useStore()
  const { guestInfo, setCouponApply } = useCart()
  const { user } = useAuth()
  const router = useRouter()

  useEffect(() => {
    localStorage.removeItem('applyVoucher')
    setCouponApply([])
  }, [])

  useEffect(() => {
    if (user) {
      getDistricts(user.provinceCode)
    }
    if (!user && guestInfo) {
      getDistricts(guestInfo.provinceCode)
    }
  }, [user, guestInfo])

  useEffect(() => {
    pixel.fbqEvent('ViewContent', {
      content_name: getTranslate(pageDetail?.data?.name),
      content_type: 'product',
    })
    return () => {
      setIsOpenComparePopup(false)
    }
  }, [pageDetail])

  if (pageDetail && pageType === PageType.PRODUCT) {
    return <ProductDetailPage data={pageDetail?.data} />
  }

  if (pageDetail && pageType === PageType.PAGE_CMS) {
    const breadcrumbs = [
      { label: getTranslate(pageDetail?.data.title) || '', href: '/' },
    ]
    return (
      <Main
        meta={
          <Meta
            title={
              getTranslate(pageDetail?.data?.metaTitle) &&
              getTranslate(pageDetail?.data?.metaTitle) !== ''
                ? getTranslate(pageDetail?.data?.metaTitle)
                : getTranslate(pageDetail?.data?.title)
            }
            description={
              getTranslate(pageDetail?.data?.metaDescription) &&
              getTranslate(pageDetail?.data?.metaDescription) !== ''
                ? getTranslate(pageDetail?.data?.metaDescription)
                : getTranslate(pageDetail?.data?.description)
            }
            noIndex={pageDetail?.data?.noIndex}
            noFollow={pageDetail?.data?.noFollow}
            keywords={getTranslate(pageDetail?.data?.keywords) || []}
            imageUrl={pageDetail?.data?.thumbnail}
            canonical={
              pageDetail?.data?.canonicalUrl ||
              process.env.NEXT_PUBLIC_URL_WEBSITE + router.asPath
            }
            mobileAlternate={{
              media: 'only screen and (max-width:991px))',
              href:
                process.env.NEXT_PUBLIC_URL_WEBSITE_MOBILE + router.asPath ||
                process.env.NEXT_PUBLIC_URL_WEBSITE_MOBILE ||
                '',
            }}
          />
        }
      >
        <section className='min-h-[500px] pb-[120px]'>
          <PageBreadcrumb homeText={t('Homepage')} breadcrumbs={breadcrumbs} />
          <h1 className='mb-[32px] text-HeadlineLarge font-bold'>
            {getTranslate(pageDetail?.data.title)}{' '}
          </h1>
          <div
            dangerouslySetInnerHTML={{
              __html: getTranslate(pageDetail?.data.content),
            }}
          ></div>
        </section>
      </Main>
    )
  }

  if (pageDetail && pageType === PageType.POST) {
    return (
      <PostDetail
        postDetail={pageDetail?.data}
        hideMenu={hideMenu}
        postCate={postCategory}
        banner={banner}
      />
    )
  }

  if (categoryDetail) {
    return (
      <CategoryPage
        pageDetail={pageDetail?.data}
        categoryDetail={categoryDetail}
        banner={banner}
        exclusiveDeal={exclusiveDeal}
      />
    )
  }
}
export default DynamicPage
export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const { req, res, query } = context
  const { token, language, messages } = await setContext(
    getCookies({ req, res }),
    {
      language: context.locale,
    }
  )
  let categoryDetail
  const { slug, hideMenu } = query
  if (!slug) {
    return {
      notFound: true,
    }
  }
  const path = slug[slug.length - 1].split('.')
  if (path.length > 1 && path[1].indexOf('c') > -1) {
    categoryDetail = await getCategorySSR(path[0], query as QueryParam)
    if (!categoryDetail) {
      return {
        notFound: true,
      }
    }
  } else {
    const pathMapping =
      Array.isArray(slug) && slug.length > 1 ? slug.join('/') : slug
    const path = await getPath(pathMapping as string)
    if (path) {
      return {
        redirect: {
          permanent: true,
          source: `/${path.path}`,
          destination: `/${path.slug || path.url}`,
        },
      }
    }
  }

  const catId = categoryDetail?._id || ''
  const params = {
    limit: 40,
    sortBy: query?.sortBy ? query?.sortBy : 'catOrder',
    sortType: query?.sortType ? query?.sortType : 'asc',
    catDisplay: true,
    ...query,
    catId,
  }
  const [
    bottomMenu,
    globalHeaderMenu,
    keywords,
    banner,
    pageDetail,
    regions,
    exclusiveDeal,
    postCategory,
  ] = await Promise.all([
    getBottomMenu(),
    getHeaderMenu(),
    getKeyword(),
    getBannerSSR(),
    getPageDetailSSR(path, params),
    getSaleRegion(),
    getExclusiveCatDeal(catId),
    getPostCategory(),
  ])
  if (!pageDetail || !pageDetail?.data) {
    return {
      notFound: true,
    }
  }
  return {
    props: {
      bottomMenu,
      globalHeaderMenu,
      keywords,
      banner,
      language,
      messages,
      pageDetail,
      pageType: getPageTypeFromPath(path),
      categoryDetail: categoryDetail || null,
      hideMenu: hideMenu || null,
      regions,
      token: token || null,
      exclusiveDeal,
      postCategory,
    },
  }
}
